import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DelayedTooltipProps {
  content: React.ReactNode;
  enterDelay?: number;
  fadeDuration?: number;
  placement?: 'top' | 'bottom';
  width?: number | string;
  disabled?: boolean;
  children: React.ReactNode;
}

const ARROW_SIZE = 8;
const GAP = 8;

const DelayedTooltip: React.FC<DelayedTooltipProps> = ({
  content,
  enterDelay = 400,
  fadeDuration = 400,
  placement = 'top',
  width = 280,
  disabled = false,
  children,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (enterTimer.current) clearTimeout(enterTimer.current);
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      if (enterTimer.current) { clearTimeout(enterTimer.current); enterTimer.current = null; }
      if (exitTimer.current) { clearTimeout(exitTimer.current); exitTimer.current = null; }
      setVisible(false);
      setMounted(false);
    }
  }, [disabled]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isTop = placement === 'top';
    setPos({
      top: isTop ? rect.top - GAP : rect.bottom + GAP,
      left: rect.left,
    });
  }, [placement]);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    if (exitTimer.current) {
      clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
    enterTimer.current = setTimeout(() => {
      updatePosition();
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    }, enterDelay);
  }, [disabled, enterDelay, updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    setVisible(false);
    exitTimer.current = setTimeout(() => setMounted(false), fadeDuration);
  }, [fadeDuration]);

  const isTop = placement === 'top';
  const numericWidth = typeof width === 'number' ? width : 280;

  const tooltipStyle: React.CSSProperties = pos ? {
    position: 'fixed',
    top: pos.top,
    left: pos.left,
    width,
    padding: '12px 14px',
    borderRadius: '10px',
    backgroundColor: '#1f2937',
    color: '#f3f4f6',
    fontSize: '12px',
    lineHeight: 1.6,
    zIndex: 9999,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    pointerEvents: 'none',
    opacity: visible ? 1 : 0,
    transform: visible
      ? `translateY(${isTop ? '-100%' : '0'})`
      : `translateY(${isTop ? 'calc(-100% + 4px)' : '-4px'})`,
    transition: `opacity ${fadeDuration}ms ease, transform ${fadeDuration}ms ease`,
  } : {};

  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    ...(isTop
      ? { bottom: -ARROW_SIZE / 2 }
      : { top: -ARROW_SIZE / 2 }),
    left: Math.min(16, numericWidth / 2),
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    backgroundColor: '#1f2937',
    transform: 'rotate(45deg)',
  };

  return (
    <span
      ref={triggerRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'help',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {mounted && pos && createPortal(
        <div style={tooltipStyle}>
          {content}
          <div style={arrowStyle} />
        </div>,
        document.body
      )}
    </span>
  );
};

export default DelayedTooltip;
